import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const includedItems = [{
  title: "Layout & structural reconfiguration",
  desc: "Opening partition walls, expanding work zones, and optimizing circulation between cooking, prep, and dining areas."
}, {
  title: "Custom & semi-custom cabinetry",
  desc: "Soft-close solid wood construction, full-height pantry storage, concealed spice pullouts, and specialized corner solutions."
}, {
  title: "Durable stone countertops",
  desc: "Fabricated quartz, natural quartzite, or granite surfaces complete with under-mount sink cutouts and matching slab backsplashes."
}, {
  title: "Appliance integration & ventilation",
  desc: "Precision framing, dedicated utility rough-ins, panel-ready installations, and high-CFM exterior ducted range hoods."
}, {
  title: "Architectural & task lighting",
  desc: "Layered illumination plans including recessed ceiling cans, dimmable under-cabinet LED runs, and island accent pendants."
}, {
  title: "Engineered & porcelain flooring",
  desc: "Water-resistant wide-plank hardwood or large-format porcelain tile laid over reinforced, level subflooring."
}];
export default function HeroKitchenRemodelAnswer() {
  return <section id="herokitchenremodelanswer" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Main Answer Column */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-[1.15]">
              Kitchen remodeling in Brookhaven
            </h1>

            <div className="mt-6 text-lg sm:text-xl text-foreground/80 leading-relaxed font-default">
              <p>
                A comprehensive kitchen remodel in Brookhaven transforms dated floor plans into an open, functional culinary center tailored to your home’s architectural character. The renovation typically encompasses full demolition down to the studs, structural wall reconfigurations, updated electrical and plumbing infrastructure, and the installation of custom cabinetry and durable solid stone surfaces. Every phase is managed to deliver an inviting, hardworking living space built to withstand daily family life and entertain with ease.
              </p>
            </div>

            {/* What's Included Block */}
            <div className="mt-10 pt-8 border-t border-border">
              <h2 className="text-xl sm:text-2xl font-serif font-medium text-foreground tracking-tight">
                What&apos;s included
              </h2>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                {includedItems.map((item, i) => <li key={item.title} data-index={i} className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-full bg-primary/15 text-primary p-1 shrink-0">
                      <Check className="size-4" strokeWidth={2.5} />
                    </div>
                    <div>
                      <span className="font-medium text-foreground block text-sm sm:text-base">
                        {item.title}
                      </span>
                      <span className="text-xs sm:text-sm text-foreground/70 leading-normal block mt-0.5">
                        {item.desc}
                      </span>
                    </div>
                  </li>)}
              </ul>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="font-medium">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an estimate
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/OurProjectsPage">
                  View recent projects
                </Link>
              </Button>
            </div>
          </div>

          {/* Realistic Visual Presentation */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_island_renovation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_island_renovation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_island_renovation.png-wvc-sizes" alt="Renovated Brookhaven kitchen featuring custom white oak cabinetry, quartz island countertops, and pendant lighting" className="w-full h-[420px] sm:h-[480px] lg:h-[540px] object-cover" loading="eager" />
            </div>
            <div className="bg-card border border-border rounded-xl p-5 shadow-xs">
              <p className="text-xs text-foreground/70 uppercase tracking-wider font-medium">
                Local craftsmanship
              </p>
              <p className="text-sm text-foreground mt-1 font-default leading-normal">
                Designed and built to municipal Brookhaven building codes, featuring certified trade partners and complete project oversight from start to final walkthrough.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}